import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, distinctUntilChanged, finalize, map, shareReplay, switchMap} from 'rxjs/operators';
import {ApiService} from './api.service';
import {Application} from '../modules/phonebook/models/application';

/**
 * Доступ к модулю компании: приложение есть в ApiService.appList$
 * (список уже с учётом module-access на бэке).
 * null в appList$ = ещё не загружали; [] = загрузили, приложений нет.
 */
@Injectable({
    providedIn: 'root',
})
export class CompanyApplicationAccessService {
    private appsRequest$: Observable<Application[]> | null = null;

    constructor(private readonly api: ApiService) {
    }

    isEnabledAndVisible(applicationName: string, apps: Application[] = this.api.appList$.getValue() || []): boolean {
        const app = apps.find((item) => item.name === applicationName);
        return !!app && app.isActive !== false && app.is_hidden !== true;
    }

    isEnabled(applicationName: string, apps: Application[] = this.api.appList$.getValue() || []): boolean {
        const app = apps.find((item) => item.name === applicationName);
        return !!app && app.isActive !== false;
    }

    watchEnabled(applicationName: string): Observable<boolean> {
        return this.ensureApps().pipe(
            switchMap(() => this.api.appList$),
            map((apps) => this.isEnabled(applicationName, apps || [])),
            distinctUntilChanged(),
        );
    }

    ensureEnabled(applicationName: string): Observable<boolean> {
        return this.ensureApps().pipe(
            map((apps) => this.isEnabled(applicationName, apps)),
        );
    }

    /** Возвращает закешированный список или один общий запрос getApplications(). */
    ensureApps(): Observable<Application[]> {
        const cached = this.api.appList$.getValue();
        if (cached !== null) {
            return of(cached);
        }

        if (!this.appsRequest$) {
            this.appsRequest$ = this.api.getApplications().pipe(
                catchError(() => of(this.api.setAppList([]))),
                finalize(() => {
                    this.appsRequest$ = null;
                }),
                shareReplay(1),
            );
        }

        return this.appsRequest$;
    }
}
